import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";
import "../css/terms.scss";

const updatedDate = "2026-08-14";
const updatedDateLabel = "14 August 2026";

function Terms() {
  const orderStages = [
    [
      "01",
      "Bag",
      "Adding an item reserves nothing; availability and price may change before checkout.",
    ],
    [
      "02",
      "Order received",
      "An automated email confirms that we received your request, not that it has been accepted.",
    ],
    [
      "03",
      "Accepted",
      "A binding sale is formed when we confirm dispatch, unless local law provides otherwise.",
    ],
    [
      "04",
      "Delivered",
      "Responsibility passes when the parcel is delivered to you or a person you nominate.",
    ],
  ];

  const sections = [
    {
      id: "agreement",
      title: "Using this store",
      content: (
        <>
          <p>
            These terms apply when you browse this website or place an order
            with {siteConfig.brandName}. By using the store, you agree to these
            terms and our <Link to="/privacy">Privacy Policy</Link>. Mandatory
            consumer rights in your country continue to apply and are not
            limited by this document.
          </p>
          <p>
            You must be able to enter into a contract in your jurisdiction and
            provide accurate, current information. If you order for another
            person, you confirm that you have their authority to do so.
          </p>
          <aside className="policy-note" aria-label="Terms summary">
            <span className="eyebrow">The short version</span>
            <p>
              Shop thoughtfully, give us accurate details, and contact us early
              if something is not right.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: "orders",
      title: "Orders & acceptance",
      content: (
        <>
          <p>
            Checkout lets you review and correct your order before submitting
            it. We may decline or cancel an order for reasons including
            unavailable stock, an obvious pricing error, failed payment,
            suspected fraud, delivery restrictions or a quantity that suggests
            resale.
          </p>
          <div className="policy-stage-grid" aria-label="Order stages">
            {orderStages.map(([number, title, copy]) => (
              <article className="policy-stage" key={title}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
          <p>
            If we cancel after payment has been authorised, the relevant amount
            will be released or refunded to the original method. Please keep
            your order number for any conversation with customer care.
          </p>
        </>
      ),
    },
    {
      id: "pricing",
      title: "Prices, promotions & payment",
      content: (
        <>
          <p>
            Store prices are shown in {siteConfig.commerce.currency}. Applicable
            tax treatment and delivery charges are presented before you confirm
            checkout. Your bank or payment provider may charge its own
            conversion or processing fee.
          </p>
          <ul className="policy-list policy-list--divided">
            <li>
              <strong>Payment</strong>
              <span>
                Must be authorised using a method available at checkout.
              </span>
            </li>
            <li>
              <strong>Promotions</strong>
              <span>
                Apply for the stated period, cannot normally be combined, and
                have no cash value.
              </span>
            </li>
            <li>
              <strong>Errors</strong>
              <span>
                Obvious pricing or description errors may be corrected before
                dispatch.
              </span>
            </li>
            <li>
              <strong>Free delivery</strong>
              <span>
                Standard delivery is free from{" "}
                {siteConfig.commerce.currencySymbol}
                {siteConfig.commerce.shippingThreshold}, subject to the shipping
                page.
              </span>
            </li>
          </ul>
        </>
      ),
    },
    {
      id: "products",
      title: "Products & pre-orders",
      content: (
        <>
          <p>
            We aim to describe colour, texture, measurements and materials
            accurately. Screens, lighting and natural variations can create
            small differences, and handmade or lower-impact materials may have
            individual character. These differences are not defects unless they
            affect normal use.
          </p>
          <p>
            Availability is not guaranteed until acceptance. A pre-order is made
            for you in a planned production run and carries an estimated
            dispatch date on the product page. We will contact you if that
            estimate changes materially; your cancellation rights remain
            available under applicable law.
          </p>
          <p>
            Product names, editorial copy, photography and sizing guidance are
            for personal shopping use. Please follow the care label to preserve
            the piece and avoid damage that falls outside a product remedy.
          </p>
        </>
      ),
    },
    {
      id: "delivery",
      title: "Delivery & risk",
      content: (
        <>
          <p>
            Available destinations, methods, estimates and final charges appear
            at checkout. Dates are estimates, not guaranteed appointments.
            Customs, weather, carrier disruption and incorrect address
            information may affect delivery. Full guidance is available on our{" "}
            <Link to="/shipping">Shipping page</Link>.
          </p>
          <p>
            Please review the address before ordering and inspect tracking
            promptly. If a parcel is late, visibly damaged or marked delivered
            but missing, contact us so we can work with the carrier. Legal
            ownership remains subject to full payment; delivery risk passes as
            required by applicable consumer law.
          </p>
        </>
      ),
    },
    {
      id: "returns",
      title: "Cancellations, returns & refunds",
      content: (
        <>
          <p>
            Eligible pieces may be returned within{" "}
            {siteConfig.commerce.returnWindow} days of confirmed delivery, in
            original condition with tags attached. Hygiene-sensitive,
            personalised, worn, washed or damaged items may be excluded where
            permitted. Read the complete{" "}
            <Link to="/returns">Returns Policy</Link>
            before sending anything back.
          </p>
          <p>
            Unless an item is faulty, incorrect or local law says otherwise,
            return postage is your responsibility. Approved refunds go to the
            original payment method. Original delivery costs are handled
            according to the reason for return and your mandatory cancellation
            rights.
          </p>
        </>
      ),
    },
    {
      id: "conduct",
      title: "Content & acceptable use",
      content: (
        <>
          <p>
            The store and its original text, design, graphics, photography,
            product presentation and software are owned by or licensed to{" "}
            {siteConfig.brandName}. You may browse and make personal purchases,
            but may not copy, scrape, republish, reverse engineer or
            commercially exploit the store without permission.
          </p>
          <p>
            Do not interfere with security, introduce harmful code, use
            automated purchasing tools, impersonate another person, submit
            unlawful content or use the service in a way that harms others. We
            may limit access where reasonably necessary to protect customers and
            the store.
          </p>
        </>
      ),
    },
    {
      id: "liability",
      title: "Responsibility, law & contact",
      content: (
        <>
          <p>
            Nothing in these terms excludes liability that cannot legally be
            excluded, including liability for fraud or for death or personal
            injury caused by negligence. To the fullest extent allowed by law,
            we are not responsible for indirect business losses, loss caused by
            misuse, or events outside reasonable control. Consumers retain all
            non-waivable remedies.
          </p>
          <p>
            These terms are governed by the laws applicable at our registered
            place of business, while you keep any mandatory protection of your
            home jurisdiction. Disputes may be brought in any court available to
            you under consumer law. We may revise these terms prospectively; the
            version accepted with an order governs that purchase.
          </p>
          <address className="policy-address">
            <strong>{siteConfig.brandName} customer care</strong>
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email}
            </a>

            <span>{siteConfig.contact.address}</span>
            <span>{siteConfig.contact.hours}</span>
          </address>
        </>
      ),
    },
  ];

  return (
    <main
      className="terms policy-page policy-page--terms"
      aria-labelledby="terms-title"
    >
      <header className="policy-hero page-shell">
        <div className="policy-hero__label">
          <p className="eyebrow">Customer care · 02</p>
          <span>A fair framework for every order.</span>
        </div>
        <div className="policy-hero__copy">
          <h1 className="page-heading" id="terms-title">
            Terms,
            <br />
            <em>in good faith.</em>
          </h1>
          <p>
            What you can expect from us—and what we ask from you—when shopping
            with {siteConfig.brandName}.
          </p>
        </div>
        <div className="policy-hero__date">
          <span className="eyebrow">Last updated</span>
          <time dateTime={updatedDate}>{updatedDateLabel}</time>
        </div>
      </header>

      <div className="policy-layout page-shell">
        <aside className="policy-toc">
          <div className="policy-toc__inner">
            <p className="eyebrow">On this page</p>
            <nav aria-label="Terms and conditions sections">
              <ol>
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="policy-toc__help">
              <span className="eyebrow">Order question?</span>
              <Link to="/contact">
                Speak with customer care <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        </aside>

        <article className="policy-content">
          {sections.map((section, index) => (
            <section
              className="policy-section"
              id={section.id}
              aria-labelledby={`${section.id}-title`}
              key={section.id}
            >
              <span className="policy-section__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="policy-section__body">
                <h2 id={`${section.id}-title`}>{section.title}</h2>
                {section.content}
              </div>
            </section>
          ))}
        </article>
      </div>

      <section
        className="policy-next page-shell"
        aria-labelledby="terms-next-title"
      >
        <div>
          <p className="eyebrow">Ready when you are</p>
          <h2 id="terms-next-title">Made to be worn, terms made to be read.</h2>
        </div>
        <div className="policy-next__links">
          <Link className="button-light" to="/shop">
            Explore the collection <span aria-hidden="true">→</span>
          </Link>
          <Link className="policy-next__text-link" to="/returns">
            Review returns <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Terms;
